import React, { useState, useGlobal, useEffect } from "reactn";
import { format, subMonths, startOfDay, parse, isValid } from "date-fns";
import { DateTime } from "luxon";

import { useDateFilter } from "../../../providers/reports";
import {
  ReportContainer,
  ReportTable,
  FilterButton,
  DateFilter
} from "../../../components/UI/reportsAnalytics";
import { useCancellableRequest } from "../../../utilities/hooks/useCancellableRequest";

const LoyaltyRewards = () => {
  const orgData = useGlobal("organizationInfo")[0];
  const [partnerPathway, setPartnerPathway] = useGlobal("partnerPathway");
  const [loading, setLoading] = useGlobal("loading");
  const [dateFrom, dateTo, setDateFrom, setDateTo] = useDateFilter();
  const { makeRequest } = useCancellableRequest();

  const [customerCreditsData, setCustomerCreditsData] = useState([]);
  const [loyaltyTypeFilter, setLoyaltyTypeFilter] = useState("credits");
  const [loyaltyTypeOptions] = useState([
    { label: "Credits", value: "credits" }
  ]);

  const [transactionsData, setTransactionsData] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const [transactionsDateFrom, setTransactionsDateFrom] = useState("");
  const [transactionsDateTo, setTransactionsDateTo] = useState("");

  const [creditsSubmitted, setCreditsSubmitted] = useState(false);
  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo] = useState("");

  const [transactionsSubmitted, setTransactionsSubmitted] = useState(false);
  const [appliedTransactionsDateFrom, setAppliedTransactionsDateFrom] =
    useState("");
  const [appliedTransactionsDateTo, setAppliedTransactionsDateTo] =
    useState("");
  const [appliedCustomers, setAppliedCustomers] = useState([]);

  useEffect(() => {
    const now = new Date();
    const lastMonth = subMonths(now, 1);
    const defaultDateFrom = format(lastMonth, "yyyy-MM-dd");
    const defaultDateTo = format(now, "yyyy-MM-dd");

    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
    setTransactionsDateFrom(defaultDateFrom);
    setTransactionsDateTo(defaultDateTo);
  }, []);

  useEffect(() => {
    setPartnerPathway([
      ...partnerPathway.slice(0, 1),
      { label: "Reports & Analytics", to: "/reporting" },
      { label: "Loyalty Rewards", to: "/reports/loyalty-rewards" }
    ]);
  }, []);

  useEffect(() => {
    if (!transactionsSubmitted) return;
    console.log("useEffect triggered with:", {
      transactionsDateFrom,
      transactionsDateTo
    });

    const fetchTransactionsData = async () => {
      console.log("fetchTransactionsData function called");

      try {
        setLoading(true);

        console.log("Fetching transactions with params:", {
          start: appliedTransactionsDateFrom,
          end: appliedTransactionsDateTo
        });

        const transactionsResponse = await makeRequest({
          method: "get",
          url: `${process.env.REACT_APP_API}/partners/orders/loyalty-transactions`,
          params: {
            start: appliedTransactionsDateFrom,
            end: appliedTransactionsDateTo,
            customerId: "all"
          }
        });

        console.log("Transactions API response:", transactionsResponse);

        if (
          transactionsResponse &&
          transactionsResponse.data &&
          transactionsResponse.data.success
        ) {
          console.log(
            "Setting transactions data:",
            transactionsResponse.data.data
          );
          console.log(
            "Data length:",
            transactionsResponse.data.data
              ? transactionsResponse.data.data.length
              : 0
          );
          setTransactionsData(transactionsResponse.data.data || []);

          const customers = [
            ...new Set(
              (transactionsResponse.data.data || []).map(t => t.customerName)
            )
          ];
          const customerOpts = customers.map(customer => ({
            label: customer,
            listingId: customer
          }));
          setAllCustomers(customerOpts);

          if (!isInitialized) {
            setSelectedCustomers(customerOpts);
            setIsInitialized(true);
          }
        } else {
          console.error(
            "API returned success: false or no response",
            transactionsResponse
          );
          setTransactionsData([]);
        }
      } catch (error) {
        console.error("Error fetching transactions data:", error);
        console.error("Error details:", {
          message: error.message,
          stack: error.stack
        });
        setTransactionsData([]);
      } finally {
        setLoading(false);
      }
    };

    if (appliedTransactionsDateFrom && appliedTransactionsDateTo) {
      console.log("Dates are set, calling fetchTransactionsData");
      fetchTransactionsData();
    } else {
      console.log("Dates are not set:", {
        transactionsDateFrom,
        transactionsDateTo
      });
    }
  }, [
    transactionsSubmitted,
    appliedTransactionsDateFrom,
    appliedTransactionsDateTo,
    appliedCustomers
  ]);
  useEffect(() => {
    if (!creditsSubmitted) return;
    const fetchCustomerCreditsData = async () => {
      try {
        setLoading(true);

        console.log("Fetching customer credits with params:", {
          start: appliedDateFrom,
          end: appliedDateTo
        });

        // get all partner connections
        const connectionsResponse = await makeRequest({
          method: "get",
          url: `${process.env.REACT_APP_API}/partners/connections/dashboard`
        });

        if (!connectionsResponse?.data?.success) {
          console.error("Failed to fetch connections");
          setCustomerCreditsData([]);
          return;
        }

        const connections = connectionsResponse.data.data.connections || [];
        console.log("Fetched connections:", connections);

        //to counter stripe rate limit
        const allClientCreditsData = [];
        const batchSize = 90;
        const batchDelay = 1000;

        for (let i = 0; i < connections.length; i += batchSize) {
          const batch = connections.slice(i, i + batchSize);
          console.log(
            `Processing batch ${Math.floor(i / batchSize) + 1}: ${
              batch.length
            } clients`
          );

          // Process all clients in the current batch concurrently
          const batchPromises = batch.map(async connection => {
            try {
              const stripeResponse = await makeRequest({
                method: "get",
                url: `${process.env.REACT_APP_API}/par/orders/stripe-customer-balance`,
                params: {
                  dsUserId: connection.dsUserId,
                  partnerId: orgData.id
                }
              });

              if (stripeResponse?.data?.success) {
                const stripeData = stripeResponse.data.data;
                const balance = -stripeData.balance || 0;
                const transactions = stripeData.balanceTransactions || [];

                // Filter transactions by date range
                const startTimestamp = Math.floor(
                  new Date(appliedDateFrom).getTime() / 1000
                );
                const endTimestamp = Math.floor(
                  new Date(appliedDateTo).getTime() / 1000
                );

                const filteredTransactions = transactions.filter(
                  transaction =>
                    transaction.created >= startTimestamp &&
                    transaction.created <= endTimestamp
                );

                let creditsSpent = 0;
                let creditsIssued = 0;

                filteredTransactions.forEach(transaction => {
                  //remeber it's backwards
                  if (transaction.amount > 0) {
                    creditsSpent += Math.abs(transaction.amount);
                  } else if (transaction.amount < 0) {
                    creditsIssued += transaction.amount;
                  }
                });

                return {
                  customerName: connection.displayName || connection.email,
                  creditBalance: balance,
                  creditsSpent: creditsSpent,
                  creditsIssued: -creditsIssued
                };
              } else {
                return {
                  customerName: connection.displayName || connection.email,
                  creditBalance: 0,
                  creditsSpent: 0,
                  creditsIssued: 0
                };
              }
            } catch (error) {
              console.error(
                `Error processing customer ${connection.email}:`,
                error
              );

              return {
                customerName: connection.displayName || connection.email,
                creditBalance: 0,
                creditsSpent: 0,
                creditsIssued: 0,
                error: "Unable to fetch Stripe data"
              };
            }
          });

          const batchResults = await Promise.all(batchPromises);
          allClientCreditsData.push(...batchResults);

          if (i + batchSize < connections.length) {
            console.log(
              `Waiting ${batchDelay}ms before processing next batch...`
            );
            await new Promise(resolve => setTimeout(resolve, batchDelay));
          }
        }

        console.log("Final client credits data:", allClientCreditsData);
        setCustomerCreditsData(allClientCreditsData);
      } catch (error) {
        console.error("Error fetching customer credits data:", error);
        setCustomerCreditsData([]);
      } finally {
        setLoading(false);
      }
    };

    if (dateFrom && dateTo) {
      fetchCustomerCreditsData();
    }
  }, [creditsSubmitted, appliedDateFrom, appliedDateTo]);
  // Helper to format dates
  const safeFormatDate = (dateString, formatString) => {
    if (!dateString) return "";
    try {
      let parsedDate;

      if (typeof dateString === "string") {
        parsedDate = new Date(dateString);
      } else {
        parsedDate = parse(dateString, "yyyy-MM-dd", new Date());
      }

      if (isValid(parsedDate)) {
        return format(parsedDate, formatString);
      }
      return "";
    } catch (error) {
      console.error(
        "Error formatting date:",
        error,
        "Date string:",
        dateString
      );
      return "";
    }
  };

  const filteredTransactions = transactionsData.filter(transaction => {
    const customerMatch =
      appliedCustomers.length === 0 ||
      appliedCustomers.length === allCustomers.length ||
      appliedCustomers.some(
        customer => customer.listingId === transaction.customerName
      );

    return customerMatch;
  });

  const getCustomerCreditsCsvData = () => {
    return customerCreditsData.map(row => ({
      customerName: row.customerName,
      creditBalance: `$${(Number(row.creditBalance || 0) / 100).toFixed(2)}`,
      creditsSpent: `$${(Number(row.creditsSpent || 0) / 100).toFixed(2)}`,
      creditsIssued: `$${(Number(row.creditsIssued || 0) / 100).toFixed(2)}`
    }));
  };

  const getTransactionsCsvData = () => {
    return filteredTransactions.map(row => ({
      customerName: String(row.customerName || "Unknown"),
      listing: String(row.listing || "Unknown"),
      plan: String(row.plan || "N/A"),
      date: String(row.date || "Unknown Date"),
      creditsUsed: `$${(Number(row.creditsUsed || 0) / 100).toFixed(2)}`
    }));
  };

  const getCustomerCreditsTableData = () => {
    return customerCreditsData.map(row => [
      row.customerName,
      `$${(Number(row.creditBalance || 0) / 100).toFixed(2)}`,
      `$${(Number(row.creditsSpent || 0) / 100).toFixed(2)}`,
      `$${(Number(row.creditsIssued || 0) / 100).toFixed(2)}`
    ]);
  };

  const getTransactionsTableData = () => {
    return filteredTransactions.map(row => [
      String(row.customerName || "Unknown"),
      String(row.listing || "Unknown"),
      safeFormatDate(row.date, "MMM d, yyyy") || "Unknown Date",
      `$${(Number(row.creditsUsed || 0) / 100).toFixed(2)}`
    ]);
  };

  const customerCreditsHeaders = [
    { label: "Client Name", key: "customerName" },
    { label: "Credit Balance", key: "creditBalance" },
    { label: "Credits Spent", key: "creditsSpent" },
    { label: "Credits Issued", key: "creditsIssued" }
  ];

  const transactionsHeaders = [
    { label: "Client Name", key: "customerName" },
    { label: "Listing", key: "listing" },
    { label: "Date", key: "date" },
    { label: "Credits Used", key: "creditsUsed" }
  ];

  const reportSubtitles = [
    orgData.organization_title,
    `For ${safeFormatDate(appliedDateFrom, "MMM d, yyyy")} - ${safeFormatDate(
      appliedDateTo,
      "MMM d, yyyy"
    )}`
  ];

  const transactionsReportSubtitles = [
    orgData.organization_title,
    `For ${safeFormatDate(
      appliedTransactionsDateFrom,
      "MMM d, yyyy"
    )} - ${safeFormatDate(appliedTransactionsDateTo, "MMM d, yyyy")}`
  ];

  return (
    <div className="admin">
      <div className="container-fluid adminprofiles">
        <div className="row cont">
          <div className="col-md-6">
            <h1>Loyalty Analytics</h1>
          </div>
        </div>

        <div className="filter-container">
          <div className="filter-section" style={{ display: "block" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "15px",
                flexWrap: "wrap",
                gap: "10px"
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                  marginRight: "15px",
                  minWidth: "160px",
                  display: "inline-block",
                  color: "white"
                }}
              >
                Client Summary:
              </span>
              <DateFilter
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
              />

              <div className="filter-item grid-new-col margin-left-offset">
                <select
                  value={loyaltyTypeFilter}
                  onChange={e => setLoyaltyTypeFilter(e.target.value)}
                  style={{
                    margin: "0 5px",
                    height: "35px",
                    width: "170px",
                    alignSelf: "center",
                    backgroundColor: "#FFF",
                    color: "#8c8ea0",
                    border: "1px solid #7ea1ff",
                    padding: "0.375rem 0.75rem",
                    fontSize: ".9rem",
                    fontWeight: "400"
                  }}
                >
                  {loyaltyTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="btn btn-primary ml-3"
                onClick={() => {
                  setAppliedDateFrom(dateFrom);
                  setAppliedDateTo(dateTo);
                  setCreditsSubmitted(true);
                }}
              >
                Apply
              </button>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px"
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                  marginRight: "15px",
                  minWidth: "160px",
                  display: "inline-block",
                  color: "white"
                }}
              >
                Transactions:
              </span>
              <DateFilter
                dateFrom={transactionsDateFrom}
                setDateFrom={setTransactionsDateFrom}
                dateTo={transactionsDateTo}
                setDateTo={setTransactionsDateTo}
              />
              <div className="filter-item grid-new-col margin-left-offset">
                <FilterButton
                  label="Filter by Client"
                  checkedOptions={selectedCustomers}
                  setCheckedOptions={setSelectedCustomers}
                  allOptions={allCustomers}
                  stringProperty="label"
                />
              </div>
              <button
                className="btn btn-primary ml-3"
                onClick={() => {
                  setAppliedTransactionsDateFrom(transactionsDateFrom);
                  setAppliedTransactionsDateTo(transactionsDateTo);
                  setAppliedCustomers(selectedCustomers);
                  setTransactionsSubmitted(true);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>

        <ReportContainer
          title="Client Credits Summary"
          subtitles={reportSubtitles}
          csvHeaders={customerCreditsHeaders}
          csvData={getCustomerCreditsCsvData()}
          fileName={`Client_Credits_Summary_${appliedDateFrom}-${appliedDateTo}.csv`}
        >
          <div className="report-table-large">
            <ReportTable
              headings={customerCreditsHeaders}
              body={getCustomerCreditsTableData()}
            />
            {!creditsSubmitted && (
              <p className="report-no-results-text">
                Select filters and click Apply to view report.
              </p>
            )}
            {customerCreditsData.length === 0 && creditsSubmitted && (
              <p className="report-no-results-text">
                No client credits data found for the selected period.
              </p>
            )}
          </div>
        </ReportContainer>

        <div style={{ marginTop: "30px" }}>
          <ReportContainer
            title="Transactions"
            subtitles={transactionsReportSubtitles}
            csvHeaders={transactionsHeaders}
            csvData={getTransactionsCsvData()}
            fileName={`Loyalty_Transactions_${appliedTransactionsDateFrom}-${appliedTransactionsDateTo}.csv`}
          >
            <div className="report-table-large">
              <ReportTable
                headings={transactionsHeaders}
                body={getTransactionsTableData()}
              />
              {!transactionsSubmitted && (
                <p className="report-no-results-text">
                  Select filters and click Apply to view report.
                </p>
              )}

              {filteredTransactions.length === 0 && transactionsSubmitted && (
                <p className="report-no-results-text">
                  No transactions found for the selected customers and period.
                </p>
              )}
            </div>
          </ReportContainer>
        </div>
      </div>
    </div>
  );
};

export default LoyaltyRewards;
